import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { watchJobs, watchJobPhotos, updateJob } from "../../services/firestore";
import ReassignGuardModal from "../../components/owner/ReassignGuardModal";
import "./OwnerLayout.css";

export default function LiveJobs() {
  const { userProfile } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pending reassignment: { job, guardNumber } when the modal is open.
  const [reassignTarget, setReassignTarget] = useState(null);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const unsub = watchJobs(userProfile.companyId, ["inProgress"], (data) => {
      setJobs(data);
      setLoading(false);
    });
    return unsub;
  }, [userProfile]);

  // Watch photos for all live jobs combined
  useEffect(() => {
    if (jobs.length === 0) { setPhotos([]); return; }
    // Subscribe to photos for each job, merge results
    const allPhotos = {};
    const unsubs = jobs.map((job) =>
      watchJobPhotos(job.id, userProfile?.companyId, (jobPhotos) => {
        allPhotos[job.id] = jobPhotos;
        const merged = Object.values(allPhotos).flat();
        merged.sort((a, b) => {
          const ta = a.takenAt?.toDate?.() || new Date(0);
          const tb = b.takenAt?.toDate?.() || new Date(0);
          return tb - ta;
        });
        setPhotos([...merged]);
      })
    );
    return () => unsubs.forEach((u) => u());
  }, [jobs, userProfile]);

  async function handleNoShow(jobId, stopIndex) {
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    const stops = [...(job.stops || [])];
    stops[stopIndex] = { ...stops[stopIndex], noShow: true, noShowAt: new Date().toISOString() };
    await updateJob(jobId, { stops });
  }

  function formatTime(ts) {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    // 24h format — house standard. Pad to HH:MM so the column aligns.
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Live jobs</div>
          <div className="page-subtitle">{jobs.length} job{jobs.length !== 1 ? "s" : ""} in progress</div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading live jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏱</div>
          <div>No jobs in progress right now</div>
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left — job cards */}
          <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {jobs.slice().sort((a, b) => {
              // Earliest scheduled first; in-progress without a schedule fall last.
              const da = a.scheduledDate || "9999-99-99";
              const db = b.scheduledDate || "9999-99-99";
              if (da !== db) return da.localeCompare(db);
              return (a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99");
            }).map((job) => {
              const stops = job.stops || [];
              const doneCount = stops.filter((s) => s.completedAt || s.noShow).length;
              const activeIdx = stops.findIndex((s) => !s.completedAt && !s.noShow);
              // Display count excludes the airport — implicit on every
              // airport job. Underlying `stops` still includes it so the
              // per-stop list below renders with airport visible.
              const realStops = stops.filter((s) => s.type !== "airport");
              const realDoneCount = realStops.filter((s) => s.completedAt || s.noShow).length;
              const pct = realStops.length > 0 ? Math.round((realDoneCount / realStops.length) * 100) : 0;
              return (
                <div key={job.id} className="panel">
                  <div style={{ padding: "12px 14px", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, color: "#185FA5", fontSize: 13 }}>{job.jobNumber}</span>
                      <span className="chip chip-teal">In progress</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#666", marginBottom: 10, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      <span>{job.brokerName || "—"}</span>
                      {(job.guardAssignments || []).slice().sort((a, b) => a.guardNumber - b.guardNumber).map((g) => (
                        <button
                          key={g.guardNumber}
                          onClick={() => setReassignTarget({ job, guardNumber: g.guardNumber })}
                          title={`Reassign Guard ${g.guardNumber}`}
                          style={{
                            background: "#eef3fb", border: "1px solid #d6e0ee",
                            color: "#185FA5", borderRadius: 12, padding: "2px 8px",
                            fontSize: 10, fontWeight: 600, cursor: "pointer",
                          }}
                        >
                          G{g.guardNumber} · {g.guardName || "—"} ↻
                        </button>
                      ))}
                      {/* Open slots (numGuards > assigned count) */}
                      {Array.from({ length: Math.max(0, (job.numGuards || 0) - (job.guardAssignments?.length || 0)) }, (_, i) => {
                        const used = new Set((job.guardAssignments || []).map((g) => g.guardNumber));
                        const next = [1, 2, 3, 4].find((n) => !used.has(n) && n <= (job.numGuards || 0));
                        if (!next) return null;
                        return (
                          <button
                            key={`open-${next}-${i}`}
                            onClick={() => setReassignTarget({ job, guardNumber: next })}
                            title={`Assign Guard ${next}`}
                            style={{
                              background: "#fff", border: "1px dashed #c0c8d4",
                              color: "#888", borderRadius: 12, padding: "2px 8px",
                              fontSize: 10, fontWeight: 600, cursor: "pointer",
                            }}
                          >
                            G{next} · open +
                          </button>
                        );
                      })}
                    </div>
                    {/* Progress bar — only on airport jobs (in/out).
                        Directs are inherently single-task; a count reads
                        as noise on the live-tracking card. */}
                    {job.jobType !== "direct" && realStops.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <div style={{ flex: 1, height: 6, background: "#e8e8e8", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: "#0B8A6A", borderRadius: 3, transition: "width 0.3s" }} />
                        </div>
                        <span style={{ fontSize: 10, color: "#888", whiteSpace: "nowrap" }}>
                          {realDoneCount} / {realStops.length} stop{realStops.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Stops list */}
                  {stops.length === 0 ? (
                    <div style={{ padding: "10px 14px", fontSize: 11, color: "#bbb" }}>No stops recorded</div>
                  ) : (
                    stops.map((stop, i) => {
                      const isDone = !!(stop.completedAt || stop.noShow);
                      const isActive = i === activeIdx;
                      return (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 14px", borderBottom: "1px solid #f8f8f8",
                          background: isActive ? "#f0faf6" : "transparent",
                        }}>
                          <div style={{
                            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                            background: stop.noShow ? "#f8d7da" : isDone ? "#d4edda" : isActive ? "#185FA5" : "#e8e8e8",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 10, color: isDone || isActive ? "#fff" : "#888",
                          }}>
                            {stop.noShow ? "✗" : isDone ? "✓" : i + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 500, color: "#111" }}>{stop.name || `Stop ${i + 1}`}</div>
                            {stop.address && <div style={{ fontSize: 10, color: "#888" }}>{stop.address}</div>}
                          </div>
                          <div style={{ fontSize: 10, color: "#888", textAlign: "right" }}>
                            {stop.noShow ? <span style={{ color: "#a32d2d" }}>No-show</span>
                              : stop.completedAt ? formatTime(stop.completedAt)
                              : isActive ? (
                                <button className="btn btn-sm" style={{ color: "#a32d2d", borderColor: "#a32d2d" }}
                                  onClick={() => handleNoShow(job.id, i)}>
                                  Mark no-show
                                </button>
                              ) : <span style={{ color: "#ccc" }}>Upcoming</span>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>

          {/* Right — live photo feed */}
          <div style={{ width: 260, borderLeft: "1px solid #e8e8e8", background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e8e8e8", flexShrink: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>Live photo feed</div>
              <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>{photos.length} photo{photos.length !== 1 ? "s" : ""}</div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {photos.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "#ccc", fontSize: 11 }}>
                  Photos taken by guards will appear here in real time
                </div>
              ) : (
                photos.map((photo) => (
                  <div key={photo.id} style={{ border: "1px solid #e8e8e8", borderRadius: 8, overflow: "hidden" }}>
                    {photo.url && (
                      <img src={photo.url} alt="job" style={{ width: "100%", display: "block", maxHeight: 160, objectFit: "cover" }} />
                    )}
                    <div style={{ padding: "6px 8px" }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#111" }}>{(photo.type || "photo").toUpperCase()}</div>
                      <div style={{ fontSize: 10, color: "#888" }}>{photo.stopName ? `${photo.stopName} · ` : ""}{jobs.find((j) => j.id === photo.jobId)?.jobNumber || photo.jobNumber || "—"}</div>
                      <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>
                        {photo.guardName || "Guard"} · {formatTime(photo.takenAt)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {reassignTarget && (
        <ReassignGuardModal
          job={reassignTarget.job}
          guardNumber={reassignTarget.guardNumber}
          onClose={() => setReassignTarget(null)}
          onDone={() => setReassignTarget(null)}
        />
      )}
    </div>
  );
}
